import { MetadataModules } from '../../enums';
import { addTypedProvider, getTyped, metadataModuleProvider, } from '../../metaData';
import { getNaturalizedNumber, getNaturalizedString, } from '../getNaturalizedField';
export const FRAME_IMAGE_IDS = MetadataModules.FRAME_IMAGE_IDS;
export const BASE_IMAGE_ID = MetadataModules.BASE_IMAGE_ID;
const BASE_IMAGE_ID_PATH_FILTER_PRIORITY = 9_000;
const BASE_IMAGE_ID_QUERY_FILTER_PRIORITY = 8_000;
const DEFAULT_FRAME_IMAGE_IDS_PRIORITY = 9_000;
function defaultBaseImageIdProvider(_next, imageId) {
    return imageId;
}
function framePathToBaseFilter(next, imageId, data, options) {
    const baseImageId = next(imageId, data, options) ?? imageId;
    return baseImageId.replace(/\/frames\/\d+(?=(\?|#|$))/i, '');
}
function frameQueryToBaseFilter(next, imageId, data, options) {
    const baseImageId = next(imageId, data, options) ?? imageId;
    return baseImageId
        .replace(/([?&])frame=\d+(&?)/i, (_match, separator, hasTrailingAmp) => {
        if (separator === '?' && hasTrailingAmp) {
            return '?';
        }
        return '';
    })
        .replace(/\?$/, '')
        .replace(/\?&/, '?')
        .replace(/&&+/g, '&');
}
function hasPhotometricInterpretation(naturalized) {
    return !!getNaturalizedString(naturalized, 'PhotometricInterpretation');
}
function addFrameQueryParameter(imageId, frameNumber) {
    const separator = imageId.includes('?') ? '&' : '?';
    return `${imageId}${separator}frame=${frameNumber}`;
}
function addDicomwebFramePath(imageId, frameNumber) {
    const instancesMatch = /\/instances\/[^/]+/i.exec(imageId);
    if (!instancesMatch) {
        return;
    }
    const insertIndex = instancesMatch.index + instancesMatch[0].length;
    return `${imageId.slice(0, insertIndex)}/frames/${frameNumber}${imageId.slice(insertIndex)}`;
}
export function generateFrameImageIdsFromNaturalized(baseImageId, naturalized) {
    if (!naturalized) {
        return;
    }
    if (!hasPhotometricInterpretation(naturalized)) {
        return new Set([baseImageId]);
    }
    const frameImageIds = new Set();
    const numberOfFrames = Math.floor(getNaturalizedNumber(naturalized, 'NumberOfFrames', 1));
    if (!Number.isFinite(numberOfFrames) || numberOfFrames < 1) {
        return;
    }
    for (let frameNumber = 1; frameNumber <= numberOfFrames; frameNumber++) {
        const framePath = addDicomwebFramePath(baseImageId, frameNumber);
        if (framePath) {
            frameImageIds.add(framePath);
            continue;
        }
        frameImageIds.add(addFrameQueryParameter(baseImageId, frameNumber));
    }
    return frameImageIds;
}
function defaultFrameImageIdsProvider(next, imageId, data, options) {
    const naturalized = metadataModuleProvider(MetadataModules.NATURALIZED, imageId, options);
    return (generateFrameImageIdsFromNaturalized(imageId, naturalized) ||
        next(imageId, data, options));
}
export function registerFrameImageIdsProvider(provider, priority = 0) {
    addTypedProvider(FRAME_IMAGE_IDS, provider, { priority });
}
export function baseImageIdQueryFilter(next, query, data, options) {
    const baseImageId = getTyped(MetadataModules.BASE_IMAGE_ID, query, options);
    return next(baseImageId, data, options);
}
export function registerImageIdTransformFilter(filter, priority = 0) {
    const provider = (next, imageId, data, options) => {
        const frameImageIds = next(imageId, data, options) ??
            new Set([imageId]);
        const produced = filter(imageId, frameImageIds);
        if (produced) {
            for (const transformedImageId of produced) {
                frameImageIds.add(transformedImageId);
            }
        }
        return frameImageIds;
    };
    registerFrameImageIdsProvider(provider, priority);
}
export function registerImageIdProviders() {
    addTypedProvider(BASE_IMAGE_ID, defaultBaseImageIdProvider, { priority: 0 });
    addTypedProvider(BASE_IMAGE_ID, framePathToBaseFilter, {
        priority: BASE_IMAGE_ID_PATH_FILTER_PRIORITY,
    });
    addTypedProvider(BASE_IMAGE_ID, frameQueryToBaseFilter, {
        priority: BASE_IMAGE_ID_QUERY_FILTER_PRIORITY,
    });
    addTypedProvider(FRAME_IMAGE_IDS, defaultFrameImageIdsProvider, {
        priority: DEFAULT_FRAME_IMAGE_IDS_PRIORITY,
    });
}
export function bulkUpdateImageIds(imageIds, updater) {
    const updatedImageIds = new Set();
    for (const imageId of imageIds) {
        const frameImageIds = getTyped(MetadataModules.FRAME_IMAGE_IDS, imageId);
        if (!frameImageIds) {
            continue;
        }
        for (const frameImageId of frameImageIds) {
            updater(frameImageId);
            updatedImageIds.add(frameImageId);
        }
    }
    return updatedImageIds;
}
